import { EventBus }     from '../../core/EventBus.js';
import { GameState }    from '../../core/GameState.js';
import { Modal }        from '../components/Modal.js';
import { showAlert }    from '../components/dialogs.js';
import { TradeRoute }   from '../../entities/TradeRoute.js';
import { TIER_ACTIONS } from '../../entities/RouteManager.js';
import { CARGO_TYPES, canShipCarryCargo }  from '../../config/cargoTypes.js';
import { isFeatureUnlocked } from '../../config/research.js';
import { formatMoney }  from '../../utils/formatting.js';
import { calculateRoute } from '../../utils/seaRoutes.js';
import { serverIntent } from '../../core/serverIntent.js';
import { debounce }     from '../../utils/debounce.js';
import { routeEditorEta } from './routeEditorEta.js';

const ACTION_LABELS = {
  buy:              'Buy Cargo',
  sell:             'Sell Cargo',
  sell_all:         'Sell All Cargo',
  refuel:           'Refuel',
  wait:             'Wait',
  warehouse_load:   'Load from Warehouse',
  warehouse_unload: 'Unload to Warehouse',
  interest_buy:     'Interest Buy',
  smart_buy:        'Smart Buy',
  smart_sell:       'Smart Sell',
  smart_refuel:     'Smart Refuel'
};

// Actions that require cargo type + quantity inputs
const ACTIONS_NEEDING_CARGO = new Set(['buy', 'sell', 'warehouse_load', 'warehouse_unload']);

// Actions that support "All Cargo" (no specific type = do all)
const ACTIONS_SUPPORTING_ALL_CARGO = new Set(['warehouse_unload', 'sell_all']);

export class RouteEditorModal {
  constructor() {
    this.modal = new Modal('route-editor-modal', { title: 'Route Editor' });
    this.route = null;
    this.isNew = false;
    this._expandedStopIdx = null;
    this._portSelectorOpen = false;
    this._portSearchTerm = '';
    this._portFilterSize = 'all';
    // Debounced so rapid clicks across the size-filter buttons don't fire
    // one network round-trip per click.
    this._saveShipFilters = debounce((shipId, filters) => {
      serverIntent('ship/ui-filters', { shipId, filters });
    }, 600);

    EventBus.on('ui:routeEditorOpen', ({ route }) => this.open(route));
    EventBus.on('ui:routeEditorNew', () => this.openNew());
  }

  openNew() {
    this.route = new TradeRoute({ name: 'New Route' });
    this.isNew = true;
    this._expandedStopIdx = null;
    this._render();
    this.modal.open();
  }

  open(route) {
    // Work on a copy to allow canceling
    this.route = new TradeRoute({
      ...route.serialize(),
      id: route.id
    });
    this.isNew = false;
    this._expandedStopIdx = null;
    this._render();
    this.modal.open();
  }

  /** Seeds _portFilterSize from the assigned ship's remembered choice, else 'all'. */
  _loadPortFilterSizeForShip() {
    const ship = this.route?.shipId ? GameState.getShip(this.route.shipId) : null;
    this._portFilterSize = ship?.uiFilters?.routeEditorSizeFilter || 'all';
  }

  _render() {
    const route = this.route;
    const eta = routeEditorEta(route);

    // Available ships: docked, not on another route (or on this route)
    const availableShips = GameState.getAllShips().filter(s => {
      if (s.assignedRouteId && s.assignedRouteId !== route.id) return false;
      return true;
    });

    // Available managers: unassigned (or assigned to this route)
    const availableManagers = [...GameState.routeManagers.values()].filter(m => {
      if (m.assignedRouteId && m.assignedRouteId !== route.id) return false;
      return true;
    });

    const selectedManager = availableManagers.find(m => m.id === route.managerId);
    const managerTier = selectedManager?.skillTier || 'junior';

    let shipOptions = '<option value="">— Select Ship —</option>';
    for (const s of availableShips) {
      const sel = s.id === route.shipId ? ' selected' : '';
      const statusLabel = s.status === 'docked'
        ? `at ${GameState.getPort(s.currentPortId)?.name || '?'}`
        : s.status;
      shipOptions += `<option value="${s.id}"${sel}>${s.name} (${s.type?.name || '?'}, ${statusLabel})</option>`;
    }

    let managerOptions = '<option value="">— Select Manager —</option>';
    for (const m of availableManagers) {
      const sel = m.id === route.managerId ? ' selected' : '';
      managerOptions += `<option value="${m.id}"${sel}>${m.name} (${m.skillTier}) — ${formatMoney(m.dailySalary)}/day</option>`;
    }

    // Stops list
    let stopsHtml = '';
    if (route.stops.length === 0) {
      stopsHtml = '<p class="panel-empty">No stops added yet. Add ports to create a route.</p>';
    } else {
      stopsHtml = route.stops.map((stop, idx) => {
        const port = GameState.getPort(stop.portId);
        const isExpanded = this._expandedStopIdx === idx;
        const actionsCount = (stop.actions || []).length;

        let actionsHtml = '';
        if (isExpanded) {
          actionsHtml = this._renderStopActions(stop, idx, managerTier);
        }

        const isPinned = stop.pinned;

        return `<div class="route-stop ${isExpanded ? 'expanded' : ''}${isPinned ? ' pinned' : ''}" data-stop-idx="${idx}">
          <div class="route-stop-header">
            <span class="stop-number">${idx + 1}</span>
            <strong class="stop-port-name" data-toggle-stop="${idx}">${port?.name || stop.portId}</strong>
            ${isPinned ? '<span class="stop-pin-badge">pinned</span>' : ''}
            <span class="stop-actions-count">${actionsCount} action${actionsCount !== 1 ? 's' : ''}</span>${eta.badgeHtml(idx)}
            <div class="stop-controls">
              ${route.mode === 'opportunistic' ? `<button class="btn btn-xs${isPinned ? ' btn-active' : ''}" data-toggle-pin="${idx}" title="${isPinned ? 'Unpin' : 'Pin (always visit)'}">P</button>` : ''}
              ${idx > 0 ? `<button class="btn btn-xs" data-move-up="${idx}" title="Move up">↑</button>` : ''}
              ${idx < route.stops.length - 1 ? `<button class="btn btn-xs" data-move-down="${idx}" title="Move down">↓</button>` : ''}
              <button class="btn btn-xs btn-danger" data-remove-stop="${idx}" title="Remove">×</button>
            </div>
          </div>
          ${actionsHtml}
        </div>`;
      }).join('');
    }

    // Port selector panel (shown when adding a stop)
    let portSelectorHtml = '';
    if (this._portSelectorOpen) {
      portSelectorHtml = this._renderPortSelector();
    }

    // Route summary
    const totalStops = route.stops.length;
    const totalActions = route.stops.reduce((s, stop) => s + (stop.actions?.length || 0), 0);

    // Interest list editor (Senior+ only)
    const showInterestList = managerTier === 'senior' || managerTier === 'expert';
    const ship = route.shipId ? GameState.getShip(route.shipId) : null;
    let interestListHtml = '';
    if (showInterestList) {
      const currentInterest = route.interestList || [];
      const interestItems = currentInterest.map((cargoId, i) => {
        const cargo = CARGO_TYPES[cargoId];
        return `<div class="interest-item" data-interest-idx="${i}">
          <span class="interest-cargo-name">${cargo?.name || cargoId}</span>
          ${i > 0 ? `<button class="btn btn-xs" data-interest-up="${i}" title="Move up">↑</button>` : ''}
          ${i < currentInterest.length - 1 ? `<button class="btn btn-xs" data-interest-down="${i}" title="Move down">↓</button>` : ''}
          <button class="btn btn-xs btn-danger" data-interest-remove="${i}" title="Remove">×</button>
        </div>`;
      }).join('');

      // Available cargo types not yet in the list (filtered by ship compatibility)
      const availableCargo = Object.entries(CARGO_TYPES)
        .filter(([id]) => !currentInterest.includes(id))
        .filter(([id]) => !ship?.type || canShipCarryCargo(ship.type, id))
        .map(([id, def]) => `<option value="${id}">${def.name}</option>`)
        .join('');

      interestListHtml = `
        <div class="interest-list-section">
          <h5>Interest List <span class="text-muted">(priority order)</span></h5>
          <p class="text-muted" style="font-size:11px;margin:0 0 6px">Manager picks the most profitable cargo from this list at each "Interest Buy" stop.</p>
          <div class="interest-items">${interestItems || '<span class="text-muted">No cargo types selected</span>'}</div>
          <div class="interest-add">
            <select id="interest-add-cargo"><option value="">— Add Cargo —</option>${availableCargo}</select>
            <button class="btn btn-xs btn-primary" id="btn-add-interest">Add</button>
          </div>
        </div>`;
    }

    this.modal.setBody(`
      <div class="route-editor">
        <div class="route-editor-left">
          <h4>Route Configuration</h4>
          <div class="route-field">
            <label>Route Name</label>
            <input type="text" id="route-name" value="${this._escapeHtml(route.name)}" maxlength="40">
          </div>
          <div class="route-field">
            <label>Ship</label>
            <select id="route-ship">${shipOptions}</select>
          </div>
          <div class="route-field">
            <label>Manager</label>
            <select id="route-manager">${managerOptions}</select>
          </div>
          <div class="route-field">
            <label class="route-toggle-label">
              <input type="checkbox" id="route-enabled" ${route.enabled ? 'checked' : ''}>
              Route Enabled
            </label>
          </div>
          ${managerTier === 'expert' ? `
          <div class="route-field">
            <label class="route-toggle-label">
              <input type="checkbox" id="route-opportunistic" ${route.mode === 'opportunistic' ? 'checked' : ''}>
              Opportunistic Mode
            </label>
            <p class="text-muted" style="font-size:11px;margin:4px 0 0">Manager chooses the most profitable stop order. Max 10 stops.${route.mode === 'opportunistic' ? ' Pin stops to ensure they are always visited.' : ''}</p>
          </div>` : ''}
          ${interestListHtml}
          <div class="route-summary">
            <span>${totalStops} stops</span>
            <span>${totalActions} actions</span>
          </div>
        </div>

        <div class="route-editor-right">
          <h4>Stops & Actions</h4>${eta.summaryHtml}
          <div class="route-stops-list">${stopsHtml}</div>
          ${this._portSelectorOpen ? '' : `
            <div class="route-add-stop">
              <button class="btn btn-sm btn-primary" id="btn-open-port-selector">+ Add Stop</button>
            </div>
          `}
          ${portSelectorHtml}
        </div>
      </div>

      <div class="route-editor-footer">
        <button class="btn btn-primary" id="btn-save-route">Save Route</button>
        <button class="btn" id="btn-cancel-route">Cancel</button>
      </div>`);

    this._bindEvents(managerTier);
  }

  _renderPortSelector() {
    const ship = this.route.shipId ? GameState.getShip(this.route.shipId) : null;

    // Reference position for distance sorting (last stop, or ship position)
    let refPos = null;
    if (this.route.stops.length > 0) {
      const lastPort = GameState.getPort(this.route.stops[this.route.stops.length - 1].portId);
      if (lastPort) refPos = lastPort.position;
    } else if (ship) {
      refPos = ship.position;
    }

    // Get & filter ports
    let ports = GameState.getAllPorts()
      .filter(p => p.size !== 'tiny')
      .filter(p => {
        if (this._portSearchTerm) {
          const t = this._portSearchTerm.toLowerCase();
          return p.name.toLowerCase().includes(t) || p.country.toLowerCase().includes(t);
        }
        return true;
      })
      .filter(p => this._portFilterSize === 'all' || p.size === this._portFilterSize);

    // Calculate distances and sort
    if (refPos) {
      for (const p of ports) {
        try { p._dist = calculateRoute(refPos, p.position).distance; }
        catch { p._dist = Infinity; }
      }
      ports.sort((a, b) => a._dist - b._dist);
    } else {
      ports.sort((a, b) => a.name.localeCompare(b.name));
    }

    const cardsHtml = ports.slice(0, 50).map(port => {
      const prices = GameState.marketPrices.get(port.id) || {};
      const stock = GameState.portStock.get(port.id) || {};
      const shipType = ship?.type;

      // Distance
      let distHtml = '';
      if (refPos && isFinite(port._dist)) {
        distHtml = `<span class="port-card-distance">${Math.round(port._dist)} nm</span>`;
      }

      // Size badge
      const sizeClass = { small: 'port-size-small', medium: 'port-size-medium', large: 'port-size-large', major: 'port-size-major' }[port.size] || '';

      // Ship compat warning
      let warning = '';
      if (shipType && !port.canAcceptShip(shipType)) {
        warning = '<span class="port-warning">Ship too large!</span>';
      }

      // Top produced (buyable cheap)
      const produced = port.producedGoods.slice(0, 4)
        .filter(id => !shipType || canShipCarryCargo(shipType, id))
        .map(id => {
          const c = CARGO_TYPES[id]; if (!c) return '';
          const p = prices[id] || c.basePrice;
          const s = stock[id] || 0;
          return `<div class="port-card-cargo"><span class="cargo-name">${c.name}</span><span class="cargo-price${p < c.basePrice * 0.9 ? ' price-good' : ''}">${formatMoney(p)}</span>${s > 0 ? `<span class="cargo-stock">(${s})</span>` : ''}</div>`;
        }).filter(Boolean).join('');

      // Top demanded (sellable high)
      const demanded = port.demandedGoods.slice(0, 4)
        .filter(id => !shipType || canShipCarryCargo(shipType, id))
        .map(id => {
          const c = CARGO_TYPES[id]; if (!c) return '';
          const p = prices[id] || c.basePrice;
          return `<div class="port-card-cargo"><span class="cargo-name">${c.name}</span><span class="cargo-price${p > c.basePrice * 1.1 ? ' price-good' : ''}">${formatMoney(p)}</span></div>`;
        }).filter(Boolean).join('');

      // Services
      const svcs = [];
      if (port.services.fuel) svcs.push(`Fuel: ${formatMoney(port.fuelPrice)}/t`);
      if (port.services.repair) svcs.push('Repair');

      return `<div class="port-card" data-select-port="${port.id}">
        <div class="port-card-header">
          <div class="port-card-title">
            <strong>${port.name}</strong>
            <span class="port-card-country">${port.country}</span>
            <span class="port-card-size ${sizeClass}">${port.size}</span>
          </div>
          <div class="port-card-meta">${distHtml}</div>
        </div>
        ${warning}
        ${produced ? `<div class="port-card-section"><h5>Produces</h5>${produced}</div>` : ''}
        ${demanded ? `<div class="port-card-section"><h5>Demands</h5>${demanded}</div>` : ''}
        ${svcs.length ? `<div class="port-card-services">${svcs.join(' · ')}</div>` : ''}
      </div>`;
    }).join('');

    return `
      <div class="port-selector">
        <div class="port-selector-header">
          <h4>Add Stop</h4>
          <button class="btn btn-xs" id="btn-close-port-selector">Cancel</button>
        </div>
        <div class="port-selector-filters">
          <input type="text" id="port-search" placeholder="Search ports..." value="${this._escapeHtml(this._portSearchTerm)}">
          <div class="port-size-filters">
            <button class="bfilter ${this._portFilterSize === 'all' ? 'active' : ''}" data-size-filter="all">All</button>
            <button class="bfilter ${this._portFilterSize === 'small' ? 'active' : ''}" data-size-filter="small">Small</button>
            <button class="bfilter ${this._portFilterSize === 'medium' ? 'active' : ''}" data-size-filter="medium">Medium</button>
            <button class="bfilter ${this._portFilterSize === 'large' ? 'active' : ''}" data-size-filter="large">Large</button>
            <button class="bfilter ${this._portFilterSize === 'major' ? 'active' : ''}" data-size-filter="major">Major</button>
          </div>
        </div>
        <div class="port-selector-list">
          ${cardsHtml || '<p class="panel-empty">No ports match filters.</p>'}
        </div>
      </div>`;
  }

  _renderStopActions(stop, stopIdx, managerTier) {
    const actions = stop.actions || [];
    let html = '<div class="stop-actions">';

    if (actions.length > 0) {
      html += actions.map((action, aIdx) => {
        const label = ACTION_LABELS[action.type] || action.type;
        const cargoName = action.cargoType === '__all__' ? 'All Cargo'
          : action.cargoType ? (CARGO_TYPES[action.cargoType]?.name || action.cargoType) : '';
        const qtyLabel = action.quantity === 'all' ? 'All'
          : action.quantity ? `${action.quantity} TEU` : '';
        let condSummary = this._conditionsSummary(action);

        return `<div class="stop-action-row">
          <span class="action-label">${label}</span>
          ${cargoName ? `<span class="action-cargo">${cargoName}</span>` : ''}
          ${qtyLabel ? `<span class="action-qty">${qtyLabel}</span>` : ''}
          ${condSummary ? `<span class="action-cond">${condSummary}</span>` : ''}
          <button class="btn btn-xs btn-danger" data-remove-action="${stopIdx}-${aIdx}" title="Remove">×</button>
        </div>`;
      }).join('');
    } else {
      html += '<p class="panel-empty" style="margin:4px 0">No actions at this stop.</p>';
    }

    // Add action form
    const allowedActions = TIER_ACTIONS[managerTier] || TIER_ACTIONS.junior;
    let actionTypeOptions = allowedActions.map(t =>
      `<option value="${t}">${ACTION_LABELS[t] || t}</option>`
    ).join('');

    let cargoOptions = '<option value="">— Cargo Type —</option>';
    // "All Cargo" option shown for warehouse_unload
    const defaultAction = allowedActions[0] || 'buy';
    const showAllCargo = ACTIONS_SUPPORTING_ALL_CARGO.has(defaultAction);
    if (showAllCargo) cargoOptions = '<option value="__all__">All Cargo</option>' + cargoOptions;
    for (const [id, def] of Object.entries(CARGO_TYPES)) {
      cargoOptions += `<option value="${id}">${def.name}</option>`;
    }

    const showCargo = ACTIONS_NEEDING_CARGO.has(defaultAction);

    html += `
      <div class="add-action-form" data-stop-idx="${stopIdx}">
        <select class="action-type-select" data-action-type-for="${stopIdx}">${actionTypeOptions}</select>
        <select class="action-cargo-select" data-action-cargo-for="${stopIdx}" ${showCargo ? '' : 'style="display:none"'}>${cargoOptions}</select>
        <input type="number" class="action-qty-input" data-action-qty-for="${stopIdx}" placeholder="Qty (TEU)" min="1" value="10" style="width:70px${showCargo ? '' : ';display:none'}">
        <label class="action-all-label" data-action-all-for="${stopIdx}" style="${showCargo ? '' : 'display:none'}"><input type="checkbox" class="action-all-check" data-action-allqty-for="${stopIdx}"> All</label>
        <button class="btn btn-xs btn-primary" data-add-action="${stopIdx}">+ Add</button>
      </div>
      <div class="add-action-conditions" data-stop-idx="${stopIdx}">
        <label>Max Price: <input type="number" data-cond-maxprice="${stopIdx}" placeholder="0" style="width:70px"></label>
        <label>Min Price: <input type="number" data-cond-minprice="${stopIdx}" placeholder="0" style="width:70px"></label>
        <label>Fuel Below %: <input type="number" data-cond-fuel="${stopIdx}" placeholder="50" style="width:50px"></label>
      </div>`;

    html += '</div>';
    return html;
  }

  _conditionsSummary(action) {
    if (!action.conditions) return '';
    const parts = [];
    if (action.conditions.maxPrice) parts.push(`≤${formatMoney(action.conditions.maxPrice)}`);
    if (action.conditions.minPrice) parts.push(`≥${formatMoney(action.conditions.minPrice)}`);
    if (action.conditions.minProfitPerTEU) parts.push(`profit≥${formatMoney(action.conditions.minProfitPerTEU)}`);
    if (action.conditions.fuelBelowPercent) parts.push(`fuel<${action.conditions.fuelBelowPercent}%`);
    if (action.conditions.maxQuantity) parts.push(`max ${action.conditions.maxQuantity}`);
    return parts.join(', ');
  }

  _bindEvents(managerTier) {
    const modal = this.modal.el;

    // Toggle stop expansion
    modal.querySelectorAll('[data-toggle-stop]').forEach(el => {
      el.addEventListener('click', () => {
        const idx = Number(el.dataset.toggleStop);
        this._expandedStopIdx = this._expandedStopIdx === idx ? null : idx;
        this._render();
      });
    });

    // Move stop up/down
    modal.querySelectorAll('[data-move-up]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.moveUp);
        if (idx > 0) {
          [this.route.stops[idx - 1], this.route.stops[idx]] = [this.route.stops[idx], this.route.stops[idx - 1]];
          if (this._expandedStopIdx === idx) this._expandedStopIdx = idx - 1;
          else if (this._expandedStopIdx === idx - 1) this._expandedStopIdx = idx;
          this._render();
        }
      });
    });

    modal.querySelectorAll('[data-move-down]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.moveDown);
        if (idx < this.route.stops.length - 1) {
          [this.route.stops[idx], this.route.stops[idx + 1]] = [this.route.stops[idx + 1], this.route.stops[idx]];
          if (this._expandedStopIdx === idx) this._expandedStopIdx = idx + 1;
          else if (this._expandedStopIdx === idx + 1) this._expandedStopIdx = idx;
          this._render();
        }
      });
    });

    // Remove stop
    modal.querySelectorAll('[data-remove-stop]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.removeStop);
        this.route.stops.splice(idx, 1);
        this._expandedStopIdx = null;
        this._render();
      });
    });

    // Open port selector
    const openSelectorBtn = document.getElementById('btn-open-port-selector');
    if (openSelectorBtn) {
      openSelectorBtn.addEventListener('click', () => {
        this._portSelectorOpen = true;
        this._portSearchTerm = '';
        this._loadPortFilterSizeForShip();
        this._render();
      });
    }

    // Close port selector
    const closeSelectorBtn = document.getElementById('btn-close-port-selector');
    if (closeSelectorBtn) {
      closeSelectorBtn.addEventListener('click', () => {
        this._portSelectorOpen = false;
        this._render();
      });
    }

    // Port search
    const searchInput = document.getElementById('port-search');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this._portSearchTerm = e.target.value;
        this._render();
        // Restore focus + cursor position
        const newInput = document.getElementById('port-search');
        if (newInput) { newInput.focus(); newInput.selectionStart = newInput.selectionEnd = newInput.value.length; }
      });
    }

    // Port size filter
    modal.querySelectorAll('[data-size-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        this._portFilterSize = btn.dataset.sizeFilter;
        if (this.route.shipId) {
          this._saveShipFilters(this.route.shipId, { routeEditorSizeFilter: this._portFilterSize });
        }
        this._render();
      });
    });

    // Select port from cards
    modal.querySelectorAll('[data-select-port]').forEach(card => {
      card.addEventListener('click', () => {
        if (this.route.mode === 'opportunistic' && this.route.stops.length >= 10) {
          showAlert('Opportunistic routes are limited to 10 stops.');
          return;
        }
        const portId = card.dataset.selectPort;
        this.route.stops.push({ portId, actions: [] });
        this._expandedStopIdx = this.route.stops.length - 1;
        this._portSelectorOpen = false;
        this._render();
      });
    });

    // Add action to a stop
    modal.querySelectorAll('[data-add-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const stopIdx = Number(btn.dataset.addAction);
        const stop = this.route.stops[stopIdx];
        if (!stop) return;

        const actionType = modal.querySelector(`[data-action-type-for="${stopIdx}"]`)?.value;
        const cargoType  = modal.querySelector(`[data-action-cargo-for="${stopIdx}"]`)?.value;
        const allQtyChecked = modal.querySelector(`[data-action-allqty-for="${stopIdx}"]`)?.checked;
        const quantity   = allQtyChecked ? 'all' : (Number(modal.querySelector(`[data-action-qty-for="${stopIdx}"]`)?.value) || 10);

        const maxPrice = Number(modal.querySelector(`[data-cond-maxprice="${stopIdx}"]`)?.value) || 0;
        const minPrice = Number(modal.querySelector(`[data-cond-minprice="${stopIdx}"]`)?.value) || 0;
        const fuelBelow = Number(modal.querySelector(`[data-cond-fuel="${stopIdx}"]`)?.value) || 0;

        const action = { type: actionType };

        // Cargo type + quantity only for buy/sell/warehouse actions
        const needsCargo = ACTIONS_NEEDING_CARGO.has(actionType);
        if (needsCargo && cargoType) action.cargoType = cargoType;
        if (needsCargo) action.quantity = quantity;

        // Conditions
        const conditions = {};
        if (maxPrice > 0) conditions.maxPrice = maxPrice;
        if (minPrice > 0) conditions.minPrice = minPrice;
        if (fuelBelow > 0) conditions.fuelBelowPercent = fuelBelow;
        if (Object.keys(conditions).length > 0) action.conditions = conditions;

        if (!stop.actions) stop.actions = [];
        stop.actions.push(action);
        this._render();
      });
    });

    // Remove action
    modal.querySelectorAll('[data-remove-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const [stopIdx, aIdx] = btn.dataset.removeAction.split('-').map(Number);
        const stop = this.route.stops[stopIdx];
        if (stop && stop.actions) {
          stop.actions.splice(aIdx, 1);
          this._render();
        }
      });
    });

    // Ship change re-renders (updates port selector compat, interest list filtering)
    const shipSelect = document.getElementById('route-ship');
    if (shipSelect) {
      shipSelect.addEventListener('change', () => {
        this.route.shipId = shipSelect.value || null;
        if (this._portSelectorOpen) this._loadPortFilterSizeForShip();
        this._render();
      });
    }

    // Manager change re-renders (updates tier-specific actions, opportunistic mode, interest list)
    const managerSelect = document.getElementById('route-manager');
    if (managerSelect) {
      managerSelect.addEventListener('change', () => {
        this.route.managerId = managerSelect.value || null;
        this._render();
      });
    }

    // Action type change — toggle cargo/qty/all visibility + update "All Cargo" option
    modal.querySelectorAll('.action-type-select').forEach(sel => {
      sel.addEventListener('change', () => {
        const stopIdx = sel.dataset.actionTypeFor;
        const cargoSel = modal.querySelector(`[data-action-cargo-for="${stopIdx}"]`);
        const qtySel = modal.querySelector(`[data-action-qty-for="${stopIdx}"]`);
        const allLabel = modal.querySelector(`[data-action-all-for="${stopIdx}"]`);
        const needsCargo = ACTIONS_NEEDING_CARGO.has(sel.value);
        if (cargoSel) {
          cargoSel.style.display = needsCargo ? '' : 'none';
          // Add/remove "All Cargo" option
          const hasAllOpt = cargoSel.querySelector('option[value="__all__"]');
          if (ACTIONS_SUPPORTING_ALL_CARGO.has(sel.value) && !hasAllOpt) {
            const opt = document.createElement('option');
            opt.value = '__all__'; opt.textContent = 'All Cargo';
            cargoSel.insertBefore(opt, cargoSel.firstChild);
          } else if (!ACTIONS_SUPPORTING_ALL_CARGO.has(sel.value) && hasAllOpt) {
            hasAllOpt.remove();
          }
        }
        if (qtySel) qtySel.style.display = needsCargo ? '' : 'none';
        if (allLabel) allLabel.style.display = needsCargo ? '' : 'none';
      });
    });

    // Opportunistic mode toggle
    const opportunisticCheckbox = document.getElementById('route-opportunistic');
    if (opportunisticCheckbox) {
      opportunisticCheckbox.addEventListener('change', () => {
        this.route.mode = opportunisticCheckbox.checked ? 'opportunistic' : 'sequential';
        if (this.route.mode === 'opportunistic' && this.route.stops.length > 10) {
          showAlert('Opportunistic routes are limited to 10 stops. Please remove some stops first.');
          this.route.mode = 'sequential';
          opportunisticCheckbox.checked = false;
        }
        this._render();
      });
    }

    // Pin/unpin stops
    modal.querySelectorAll('[data-toggle-pin]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = Number(btn.dataset.togglePin);
        const stop = this.route.stops[idx];
        if (stop) {
          stop.pinned = !stop.pinned;
          this._render();
        }
      });
    });

    // Interest list: add cargo
    const addInterestBtn = document.getElementById('btn-add-interest');
    if (addInterestBtn) {
      addInterestBtn.addEventListener('click', () => {
        const cargoId = document.getElementById('interest-add-cargo')?.value;
        if (!cargoId) return;
        if (!this.route.interestList) this.route.interestList = [];
        if (!this.route.interestList.includes(cargoId)) {
          this.route.interestList.push(cargoId);
          this._render();
        }
      });
    }

    // Interest list: remove, move up/down
    modal.querySelectorAll('[data-interest-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.interestRemove);
        this.route.interestList.splice(idx, 1);
        this._render();
      });
    });
    modal.querySelectorAll('[data-interest-up]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.interestUp);
        if (idx > 0) {
          [this.route.interestList[idx - 1], this.route.interestList[idx]] =
            [this.route.interestList[idx], this.route.interestList[idx - 1]];
          this._render();
        }
      });
    });
    modal.querySelectorAll('[data-interest-down]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.interestDown);
        if (idx < this.route.interestList.length - 1) {
          [this.route.interestList[idx], this.route.interestList[idx + 1]] =
            [this.route.interestList[idx + 1], this.route.interestList[idx]];
          this._render();
        }
      });
    });

    // Save route
    const saveBtn = document.getElementById('btn-save-route');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this._saveRoute());
    }

    // Cancel
    const cancelBtn = document.getElementById('btn-cancel-route');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.modal.close());
    }
  }

  async _saveRoute() {
    const route = this.route;

    // Read current form values
    route.name      = document.getElementById('route-name')?.value || 'Unnamed Route';
    route.shipId    = document.getElementById('route-ship')?.value || null;
    route.managerId = document.getElementById('route-manager')?.value || null;
    route.enabled   = document.getElementById('route-enabled')?.checked || false;

    // Incomplete routes are saved as drafts (cannot be enabled)
    const isDraft = !route.shipId || !route.managerId || route.stops.length < 2;
    if (isDraft && route.enabled) {
      route.enabled = false;
    }

    let result;
    if (this.isNew || !GameState.tradeRoutes.has(route.id)) {
      result = await serverIntent('route/create', {
        stops:        route.stops,
        mode:         route.mode,
        interestList: route.interestList,
        managerId:    route.managerId,
        shipId:       route.shipId,
        name:         route.name,
        enabled:      route.enabled
      });
    } else {
      result = await serverIntent('route/update', {
        routeId:      route.id,
        name:         route.name,
        shipId:       route.shipId,
        managerId:    route.managerId,
        enabled:      route.enabled,
        stops:        route.stops,
        mode:         route.mode,
        interestList: route.interestList
      });
    }

    if (result.success) {
      // Optimistic local update so the panel re-renders immediately
      if (result.route) {
        // Server returned the canonical route — use it
        const { TradeRoute } = await import('../../entities/TradeRoute.js');
        const canonical = TradeRoute.deserialize(result.route);
        GameState.tradeRoutes.set(canonical.id, canonical);
        const ship = GameState.getShip(canonical.shipId);
        if (ship) ship.assignedRouteId = canonical.id;
        const manager = GameState.routeManagers.get(canonical.managerId);
        if (manager) manager.assignedRouteId = canonical.id;
      } else {
        // Fallback: store locally as before
        GameState.tradeRoutes.set(route.id, route);
      }
      EventBus.emit('route:saved', { route });
      this.modal.close();
    }
    // serverIntent shows toast on failure
  }

  _escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
